<template>
  <div class="my-body">
    <div class="my-info-item">
      <p class="title">{{ t('label_basic_info') }}</p>
      <Info2Column title="ID">
        <p>{{ userId }}</p>
      </Info2Column>

      <Info2Column :title="t('label_name')">
        <input
          v-model="userName"
          type="text"
          :placeholder="t('alert_enter_name')"
          :readonly="!isFocused"
          @focus="isFocused = true"
          @blur="isFocused = false"
        />
      </Info2Column>

      <Info2Column v-if="domain === 'SELF_SERVICE'" :title="t('label_email')">
        <input v-model="userEmail" type="text" :placeholder="t('alert_enter_email')" />
      </Info2Column>

      <Info2Column v-if="domain === 'SELF_SERVICE' && currentTeamId" :title="t('organization_id')">
        <p>{{ currentTeamId }}</p>
      </Info2Column>

      <template v-if="showPasswordEdit">
        <Info2Column :title="t('label_password')">
          <input
            v-model="pw01"
            :type="showPassword[0] ? 'text' : 'password'"
            :placeholder="t('alert_enter_password')"
            :readonly="!isFocused"
            @focus="isFocused = true"
            @keydown="blockInputInvalidPassword"
            @blur="isFocused = false"
          />
          <EyeIcon class="eye-icon icon-dim" :showPassword="showPassword[0]" @click="togglePasswordVisibility(0)" />
        </Info2Column>

        <Info2Column :title="t('alert_confirm_password')">
          <input
            v-model="pw02"
            :type="showPassword[1] ? 'text' : 'password'"
            class="text-input"
            :placeholder="t('alert_reenter_password')"
            :readonly="!isFocused"
            @focus="isFocused = true"
            @keydown="blockInputInvalidPassword"
            @blur="isFocused = false"
            @paste.prevent
          />
          <EyeIcon class="eye-icon icon-dim" :showPassword="showPassword[1]" @click="togglePasswordVisibility(1)" />
        </Info2Column>
      </template>
    </div>

    <div class="my-info-item">
      <p class="title">{{ t('label_additional_info') }}</p>

      <Info2Column :title="t('label_language')">
        <div ref="dropdownContainerRef" :class="{ active: showDropdown }" :data-off="!showDropdown">
          <button
            ref="triggerRef"
            class="button-dim bk-line-btn"
            :class="{ active: showDropdown }"
            @click="toggleDropdown"
          >
            <p>
              {{ currentLanguage ? currentLanguage.name : t('alert_select_language') }}
            </p>
            <component :is="showDropdown ? ChevronUpIcon : ChevronDownIcon" />
          </button>

          <Dropdown
            v-if="showDropdown"
            :trigger-ref="triggerRef"
            :class="{ active: showDropdown }"
            @close="closeDropdown"
          >
            <LanguageList
              :languages="PAGE_LANGUAGES"
              :showAllInList="true"
              :selectedAtTop="true"
              @select="selectLanguage"
            />
          </Dropdown>
        </div>
      </Info2Column>

      <Info2Column v-if="domain === 'SELF_SERVICE' && adminStore.isHost" :title="t('label_country')">
        <DropdownCountry v-model="currentCountryCode" />
      </Info2Column>

      <Info2Column v-if="domain === 'ADMIN'" :title="t('label_recognize_my_voice')">
        <div class="voice-uploader-wrapper">
          <button class="button-secondary" @click="handleAddVoiceClick">
            {{ voiceRegistered ? t('label_rerecognize_my_voice') : t('label_recognize_my_voice') }}
          </button>
          <p v-if="voiceRegistered" class="small-comment small">
            {{ t('label_voice_already_registered') }}
          </p>
        </div>
      </Info2Column>

      <Info2Column v-if="domain === 'SELF_SERVICE' && adminStore.isHost" :title="t('save_transcription_title')">
        <div class="wrapper-save-history">
          <Checkbox v-model="termStatus.storeAgreement">
            {{ t('save_transcription_effect') }}
          </Checkbox>
          <p class="small dim">
            {{ t('interpretation_record_description_1') }} <br />
            {{ t('interpretation_record_description_2') }}
            <i18n-t keypath="interpretation_record_description_3">
              <template #policy>
                <a class="link-small" @click="openPolicyPage">
                  {{ t('privacy_policy') }}
                </a>
              </template>
            </i18n-t>
          </p>
        </div>
      </Info2Column>

      <Info2Column v-if="domain === 'SELF_SERVICE' && adminStore.isHost" :title="t('marketing_receive')">
        <div class="wrapper-save-history">
          <Checkbox v-model="termStatus.marketingAgreement">
            {{ t('agree_marketing_policy') }}
          </Checkbox>
        </div>
      </Info2Column>
    </div>

    <template v-if="domain === 'SELF_SERVICE' && adminStore.isHost">
      <div class="my-info-item">
        <p class="title">{{ t('account_management') }}</p>
        <button class="text-btn" @click="handleAccountDelete">
          {{ t('button_account_delete') }}
        </button>
      </div>
    </template>

    <AddVoiceModal
      v-if="showVoiceModal"
      :long-voice="true"
      :device="microphoneStore.selectedMicrophone"
      @close="closeGroupModal"
    />
  </div>

  <div class="footer-wrap">
    <button v-if="domain !== 'SELF_SERVICE'" class="gray-btn m-btn" @click="goBack">
      {{ t('label_exit') }}
    </button>
    <ButtonProcess :loading="isSaving" @click="onSave">
      {{ t('button_save2') }}
    </ButtonProcess>
  </div>

  <ModalAccountDelete v-if="isVisibleModalAccountDelete" @close="closeDeleteModal" />
</template>

<script lang="ts" setup>
import api from '@/api';
import { updateEmployee } from '@/api/apiService';
import AddVoiceModal from '@/components/domains/admin/common/AddVoiceModal.vue';
import ModalAccountDelete from '@/components/domains/self-service/ModalAccountDelete.vue';
import ChevronDownIcon from '@/components/icons/IconChevronDown.vue';
import ChevronUpIcon from '@/components/icons/IconChevronUp.vue';
import EyeIcon from '@/components/icons/IconEye.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import Checkbox from '@/components/shared/base/Checkbox.vue';
import Dropdown from '@/components/shared/base/Dropdown.vue';
import LanguageList from '@/components/shared/base/LanguageList.vue';
import DropdownCountry from '@/components/shared/composite/DropdownCountry.vue';
import Info2Column from '@/components/shared/composite/Info2Column.vue';
import { useModalAccountDelete } from '@/composables/domains/self-service/useModalAccountDelete';
import { useModalPolicy } from '@/composables/domains/self-service/useModalPolicy';
import { useModalSurvey } from '@/composables/domains/self-service/useModalSurvey';
import { PAGE_LANGUAGES } from '@/static/languages';
import { useAllTranslations } from '@/static/messageBylanguages.js';
import { useAdminStore } from '@/stores/admin';
import { useMicrophoneStore } from '@/stores/microphone';
import { useToastStore } from '@/stores/toast.js';
import { useUserStore } from '@/stores/user';
import { Language, TypeDomain } from '@/types/common';
import { IResponsePartnerEmployee } from '@/types/response';
import { encryptPassword } from '@/utils/encryptor';
import { blockInputInvalidPassword } from '@/utils/validatorInput';
import { validatePassword, validatePasswordConfirm } from '@/utils/validators.ts';

const props = withDefaults(
  defineProps<{
    domain: TypeDomain;
  }>(),
  {
    domain: 'ADMIN',
  }
);
const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();
const router = useRouter();

const toastStore = useToastStore();
const adminStore = useAdminStore();
const userStore = useUserStore();
const microphoneStore = useMicrophoneStore();

const translations = useAllTranslations();
const { setTypeSurveyDeleteAccount, openModalSurvey } = useModalSurvey();
const { isVisibleModalAccountDelete, closeModalAccountDelete } = useModalAccountDelete();
const { openModalPolicy, setPolicyCurrent } = useModalPolicy();

const showVoiceModal = ref(false);
const showPassword = ref([false, false]);

const userId = ref(''); // 아이디
const userName = ref(userStore.userInfo.nickname); // 이름
const userEmail = ref(userStore.userInfo.email); // 이메일
const pw01 = ref(''); // 비밀번호
const pw02 = ref(''); // 비밀번호 확인
const termStatus = ref<Record<string, boolean>>({
  storeAgreement: false,
  marketingAgreement: false,
});
const currentCountryCode = ref<string>(''); // 현재 국가
const currentTeamId = ref<string>(''); // 현재 팀아이디
const currentLanguage = ref<Language>(null); // 현재 언어

const partnerEmployeeInfo = ref<IResponsePartnerEmployee>({
  languageCode: '',
  nickname: '',
  partnerGroups: [],
  userId: '',
  userType: '',
});

const isFocused = ref(false);
const voiceRegistered = ref(false);
const isSaving = ref(false);

const showDropdown = ref(false);
const dropdownContainerRef = ref(null);
const triggerRef = ref(null);

const showPasswordEdit = computed<boolean>(() => {
  return (
    {
      ADMIN: true,
      SELF_SERVICE: !adminStore.isHost,
    }[props.domain] || false
  );
});

const openPolicyPage = () => {
  setPolicyCurrent('privacy');
  openModalPolicy();
};

const goBack = () => {
  router.go(-1);
};

const getPartnerHome = async () => {
  await adminStore.fetchPartnerHome();
  partnerEmployeeInfo.value = adminStore.getPartnerEmployeeInfo;

  const { success, registered } = await api.get(`/api/chat/business/call/voice/registered/any`);
  if (success) voiceRegistered.value = registered;
};

const selectLanguage = (language) => {
  currentLanguage.value = language;
  showDropdown.value = false;
};

const notifyError = (messageKey: string) => {
  toastStore.error(t(messageKey));
  return false;
};

const validate = () => {
  if (!userName.value) {
    return notifyError('alert_enter_name');
  }

  if (userName.value.length < 2 || userName.value.length > 20) {
    return notifyError('alert_name_length_limit');
  }

  if (!currentLanguage.value) {
    return notifyError('alert_select_language');
  }

  if (pw01.value || pw02.value) {
    const pwError = validatePassword(pw01.value);
    if (pwError) {
      return notifyError(pwError);
    }

    const pwConfirmError = validatePasswordConfirm(pw01.value, pw02.value);
    if (pwConfirmError) {
      return notifyError(pwConfirmError);
    }
  }

  return true;
};

const togglePasswordVisibility = (idx) => {
  showPassword.value[idx] = !showPassword.value[idx];
};

const onSave = async () => {
  if (!validate() || isSaving.value) {
    return;
  }

  isSaving.value = true;

  const response = await api.get('/api/partner/auth/keys');
  if (response.success) {
    const publicKey = response.data.partnerPublicKey;
    const encryptedPassword = pw01.value ? await encryptPassword(publicKey, pw01.value) : null;

    const editResponse = await updateEmployee(partnerEmployeeInfo.value.userId.trim(), encryptedPassword, {
      body: JSON.stringify({
        nickname: userName.value,
        email: userEmail.value,
        languageCode: currentLanguage.value.code,
        groupIds: [],
        sampleRate: '16000',
        storeAgreement: termStatus.value.storeAgreement,
        marketingAgreement: termStatus.value.marketingAgreement,
        countryCode: currentCountryCode.value,
      }),
      voice: '',
    });

    if (editResponse.success) {
      await userStore.fetchUserInfo();

      toastStore.info(translations.alert_change_completed.value);
      getPartnerHome();

      if (props.domain !== 'SELF_SERVICE') {
        goBack();
      }
    }
  }

  isSaving.value = false;
};

const handleAddVoiceClick = () => {
  showVoiceModal.value = true;
};

const closeGroupModal = (registered) => {
  if (registered) {
    voiceRegistered.value = true;
  }

  showVoiceModal.value = false;
};

const closeDeleteModal = (isAll: boolean = false) => {
  closeModalAccountDelete();
  if (isAll) {
    emit('close');
  }
};

const handleAccountDelete = () => {
  setTypeSurveyDeleteAccount();
  openModalSurvey();
};

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
};

const closeDropdown = () => {
  showDropdown.value = false;
};

const handleClickOutside = (event) => {
  if (dropdownContainerRef.value && !dropdownContainerRef.value.contains(event.target)) {
    closeDropdown();
  }
};

watch(showDropdown, (newVal) => {
  if (newVal) {
    document.addEventListener('click', handleClickOutside);
  } else {
    document.removeEventListener('click', handleClickOutside);
  }
});

onMounted(async () => {
  await getPartnerHome();
  await userStore.fetchUserInfo();

  const {
    storeAgreement = false,
    marketingAgreement = false,
    countryCode = '',
    teamId = '',
  } = userStore.userInfo || {};
  termStatus.value.storeAgreement = storeAgreement;
  termStatus.value.marketingAgreement = marketingAgreement;

  currentCountryCode.value = countryCode;
  currentTeamId.value = teamId;

  userId.value = partnerEmployeeInfo.value.loginId;
  userName.value = partnerEmployeeInfo.value.nickname;

  currentLanguage.value = PAGE_LANGUAGES.find(
    (lang) => lang.code === partnerEmployeeInfo.value.languageCode
  ) as Language;
});
</script>

<style scoped lang="scss">
.my-body {
  padding-bottom: 65px;
  .my-info-item {
    padding: 2rem 1rem;
    border-bottom: 1px solid $color-gray-40;
    &:last-child {
      border-bottom: 0;
    }
    .title {
      font-weight: 600;
    }
  }
}

.footer-wrap {
  position: absolute;
  width: calc(100% - 200px);
  background: #fff;
  border-top: 1px solid $color-gray-40;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  bottom: 0;
  padding: 0.75rem 1rem;
  button {
    width: auto;
    padding: 0 1.5rem;
    height: 40px !important;
    font-size: $font-size-psmall;
  }
}

.eye-icon {
  position: absolute;
  right: 0.5rem;
  bottom: 0;
  transform: translateY(-50%);
  width: 1.5rem;
  height: 1.5rem;
  cursor: pointer;
}

.wrapper-save-history {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.voice-uploader-wrapper {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 1rem;
}

.small-comment {
  display: block;
  position: relative;
}

/* 드랍다운 */
.dropdown-trigger {
  display: flex;
  width: fit-content;
  gap: 0.5rem;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
}

.dropdown-trigger:hover {
  background-color: var(--color-gray-10);
}

.dropdown-trigger:active {
  background-color: var(--color-gray-10);
}

.dropdown-trigger.active {
  background-color: var(--color-gray-10);
}

.bk-line-btn {
  border: 1px solid $color-gray-900;
  background: #fff;
  height: 48px;
  padding: 0 0.75rem;
  width: 100%;
  display: flex;
  justify-content: space-between;
}

::v-deep(.content-slot) {
  overflow-x: unset;
}
</style>
